import { HTMLMotionProps, motion } from 'framer-motion';
import styled from 'styled-components';

export const Container = styled.main<{ shouldDisplayStepCard: boolean }>`
  background: ${({ theme }) => theme.palette.white._300.hex()};
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: ${({ shouldDisplayStepCard }) =>
    shouldDisplayStepCard ? '1fr 320px' : '1fr'};
  column-gap: 1rem;
  padding: 2rem;
  overflow: hidden;

  &::-webkit-scrollbar-thumb {
    height: 10px;
    border-radius: 10px;
    background: ${({ theme }) => theme.palette.blue._400.hex()};
    &:hover {
      background: ${({ theme }) => theme.palette.blue._400.hex()};
    }
  }

  @media (max-width: 900px) {
    flex-direction: column-reverse;
    display: flex;
    gap: 1rem;
  }
`;

type CardContainerProps = HTMLMotionProps<'div'> & {
  $noMobile?: boolean;
};

export const CardHeaderContainer = styled(motion.div)<CardContainerProps>`
  background: ${({ theme }) => theme.palette.white.main.hex()};
  border-radius: 10px;
  padding: 16px 24px;
  border: 1px solid ${({ theme }) => theme.palette.grey._200?.hex() || '#e2e8f0'};
  box-shadow: 0px 1px 0px rgba(0, 0, 0, 0.25);

  @media (max-width: 900px) {
    display: ${({ $noMobile }) => ($noMobile ? 'none' : 'block')};
    padding: 16px;
  }
`;

export const IconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 40px;
  height: 40px;

  border-radius: 12px;
  background-color: #eff6ff;

  color: #155dfc;

  & > svg {
    width: 20px;
    height: 20px;
    stroke-width: 2px; /* Se usar ícones de linha */
  }
`;

export const CardContainer = styled(motion.div)<CardContainerProps>`
  background: ${({ theme }) => theme.palette.white.main.hex()};
  border-radius: 10px;
  padding: 24px;

  @media (max-width: 900px) {
    display: ${({ noMobile }) => noMobile && 'none'};
  }

  &::-webkit-scrollbar-thumb {
    height: 10px;
    border-radius: 10px;
    background: ${({ theme }) => theme.palette.blue._400.hex()};
    &:hover {
      background: ${({ theme }) => theme.palette.blue._400.hex()};
    }
  }

  scrollbar-color: ${({ theme }) => theme.palette.blue._400.hex()}
    ${({ theme }) => theme.palette.grey._400.hex()};
`;

export const CardContainerV2 = styled(motion.div)<CardContainerProps>`
  background: ${({ theme }) => theme.palette.white.main.hex()};
  border-radius: 10px;
  padding: 2rem;

  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 900px) {
    display: ${({ noMobile }) => noMobile && 'none'};
  }

  &::-webkit-scrollbar-thumb {
    height: 10px;
    border-radius: 10px;
    background: ${({ theme }) => theme.palette.blue._400.hex()};
    &:hover {
      background: ${({ theme }) => theme.palette.blue._400.hex()};
    }
  }

  scrollbar-color: ${({ theme }) => theme.palette.blue._400.hex()}
    ${({ theme }) => theme.palette.grey._400.hex()};

  h3 {
    color: #0043ce;
    font-size: 16px;
  }

  .label {
    font-size: 1rem !important;
    font-weight: 600 !important;
    color: #394760 !important;
    font-family: 'Inter' !important;
  }
`;

export const DamageTableContainer = styled.div`
  border: 1px solid #c6c6c6;
  border-radius: 8px;
  overflow: hidden;
`;

export const DamageTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const DamageThead = styled.thead`
  background-color: #e0e0e0;
`;

export const DamageTh = styled.th`
  padding: 16px 12px;
  text-align: left;
  font-size: 16px;
  font-weight: 700;
  color: #364153;
`;

export const DamageTd = styled.td`
  padding: 16px;
  vertical-align: top;

  &:last-child {
    text-align: center;
    border-right: none;
    width: 1px;
    white-space: nowrap;

    > button {
      margin: 0 auto;
    }
  }
`;

export const AddButtonContainer = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;

  background-color: #1a66ff;
  color: #ffffff;

  border: none;
  border-radius: 10px;
  padding: 10px 16px;

  font-size: 14px;
  font-weight: 400;

  cursor: pointer;
`;

export const IconButton = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  cursor: pointer;
  color: #c5c5c5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  width: 32px;
  height: 32px;

  > svg {
    height: 16px;
    width: 16px;
  }

  > .upload-icon:hover {
    color: #0055ff;
  }

  > .trash-icon:hover {
    color: #da1e28;
  }
`;

export const Title = styled.h1`
  font-size: 2rem;
  color: #394760;
  font-family: 'Inter';
`;

export const StepperContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  width: 100px;
  padding: 4px;
`;

export const StepperBtn = styled.button.attrs({ type: 'button' })`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;

  &:hover {
    color: #111827;
    background-color: #e5e7eb;
    border-radius: 4px;
  }
`;

export const StepperValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #111827;
`;

export const ButtonContainer = styled.div`
  width: 20%;
  @media (max-width: 1500px) {
    width: 30%;
  }
  @media (max-width: 1000px) {
    width: 40%;
  }
  @media (max-width: 900px) {
    width: 100%;
  }
`;

export const Subtitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 500;
  color: #394760;
  margin-bottom: 1rem;
  font-family: 'Inter';
`;

export const CardsContainer = styled.div`
  overflow: hidden;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;

  @media (max-width: 900px) {
    height: 100%;
    width: 100%;
  }
`;

export const BackInitialButton = styled.button`
  margin-top: 1rem;
  color: #0d4bd0;
  cursor: pointer;
  border: none;
  text-decoration: underline;

  display: flex;
  align-items: center;
  background-color: transparent;
`;
